// ============================================
// Form WebSocket Updates - ENABLED
// Real-time form field updates from LLM extraction
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { getWebSocketUrl, getApiBaseUrl } from '../utils/websocket';

export interface FormData {
  [tabName: string]: {
    [fieldName: string]: {
      value: any;
      confidence: number;
      source: string;
    };
  };
}

interface FormDataContextType {
  formData: FormData;
  isConnected: boolean;
  error: string | null;
  updateField: (tabName: string, fieldName: string, value: any) => void;
  getFieldValue: (tabName: string, fieldName: string, defaultValue?: any) => any;
  getFieldConfidence: (tabName: string, fieldName: string) => number;
  getFieldSource: (tabName: string, fieldName: string) => string;
  hasLowConfidence: (tabName: string, fieldName: string, threshold?: number) => boolean;
  refreshFromDatabase: () => Promise<void>;
  saveFormData: () => Promise<{ success: boolean; error?: string }>;
  clearFormForTab: (tabName: string) => void;
}

export function useFormUpdates(sessionId: string | null): FormDataContextType {
  const [formData, setFormData] = useState<FormData>({});
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  /** Last session id we merged into form state — do not carry `manual` fields from prev when this changes (e.g. live → past visit). */
  const lastMergedSessionIdRef = useRef<string | null>(null);
  /** Synchronous pointer to the session id React is showing — used to drop out-of-order load results (e.g. Edit form switches `effectiveFormSessionId` while an older fetch is still in flight). */
  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = sessionId;
  const prevHookSessionIdRef = useRef<string | null>(null);

  // ==========================================
  // Load data from database
  // ==========================================
  const loadFromDatabase = useCallback(async () => {
    if (!sessionId) return;
    const startedFor = sessionId;

    try {
      console.log('[useFormUpdates] 📚 Loading data from database...');

      const apiBaseUrl = getApiBaseUrl();
      console.log('[useFormUpdates] 📥 API Base URL:', apiBaseUrl);

      // STEP 1: Load extracted fields from LLM (extracted_fields table)
      // This includes ALL LLM extractions, even those not yet mapped to domain tables
      console.log('[useFormUpdates] 📥 Loading extracted fields from session...');
      const extractedFieldsResponse = await fetch(
        `${apiBaseUrl}/forms/${sessionId}/data`,
        {
          headers: { 'ngrok-skip-browser-warning': 'true' },
        }
      );

      let mergedData: FormData = {};

      if (extractedFieldsResponse.ok) {
        const extractedData = await extractedFieldsResponse.json();
        if (Object.keys(extractedData).length > 0) {
          console.log('[useFormUpdates] ✅ Loaded extracted fields:', Object.keys(extractedData));
          mergedData = { ...extractedData };
        }
      }

      // STEP 2: Load complete patient data from domain tables
      // This includes historical patient data and domain-specific fields
      const sessionResponse = await fetch(`${apiBaseUrl}/sessions/${sessionId}`, {
        headers: { 'ngrok-skip-browser-warning': 'true' },
      });

      let sessionStatus: string | undefined;
      if (sessionResponse.ok) {
        const session = await sessionResponse.json();
        sessionStatus = typeof session.status === 'string' ? session.status : undefined;
        const patientId = session.patient_id;

        if (patientId) {
          const params = new URLSearchParams();
          if (session.visit_id) params.set('visit_id', session.visit_id);
          if (sessionId) params.set('session_id', sessionId);
          const queryString = params.toString() ? `?${params.toString()}` : '';
          const endpoint = `${apiBaseUrl}/api/patient-data/${patientId}/2tab-form${queryString}`;

          console.log('[useFormUpdates] 📥 Loading patient data from domain tables (2-tab)...');
          const patientDataResponse = await fetch(endpoint, {
            headers: { 'ngrok-skip-browser-warning': 'true' },
          });

          if (patientDataResponse.ok) {
            const patientData = await patientDataResponse.json();

            if (Object.keys(patientData).length > 0) {
              console.log('[useFormUpdates] ✅ Loaded patient data:', Object.keys(patientData));

              // MERGE: Domain table data fills in missing fields, but preserves
              // extracted field metadata (source, confidence) when values match.
              // This keeps the confidence chip visible for AI-extracted fields.
              for (const [tabName, fields] of Object.entries(patientData as FormData)) {
                if (!mergedData[tabName]) {
                  mergedData[tabName] = {};
                }
                for (const [fieldName, domainField] of Object.entries(fields as FormData[string])) {
                  const existing = mergedData[tabName][fieldName];
                  if (
                    existing &&
                    existing.source !== 'manual' &&
                    existing.source !== 'database' &&
                    String(existing.value) === String(domainField.value)
                  ) {
                    // Same value already present from extracted fields — keep original
                    // source & confidence so the confidence chip stays visible
                    continue;
                  }
                  mergedData[tabName][fieldName] = domainField;
                }
              }
            }
          }
        }
      }

      // Default Consultation.date_of_visit to today only for an active session (not when editing completed/past visit)
      const isActiveSession = (sessionStatus || 'active').toLowerCase() === 'active';
      // Keep visit date/time shared between PreConsultation and Consultation tabs.
      // Canonical source priority:
      // - visit_date/date_of_visit: PreConsultation.visit_date > Consultation.date_of_visit
      // - arrival_time: PreConsultation.arrival_time > Consultation.arrival_time
      {
        const preVisitDate = mergedData?.PreConsultation?.visit_date?.value;
        const consultVisitDate = mergedData?.Consultation?.date_of_visit?.value;
        const hasPreDate = preVisitDate != null && String(preVisitDate).trim() !== '';
        const hasConsultDate = consultVisitDate != null && String(consultVisitDate).trim() !== '';
        if (hasPreDate || hasConsultDate) {
          const canonicalDate = hasPreDate ? preVisitDate : consultVisitDate;
          const dateSource =
            mergedData?.PreConsultation?.visit_date?.source ||
            mergedData?.Consultation?.date_of_visit?.source ||
            'database';
          if (!mergedData.PreConsultation) mergedData.PreConsultation = {};
          if (!mergedData.Consultation) mergedData.Consultation = {};
          mergedData.PreConsultation.visit_date = { value: canonicalDate, confidence: 1.0, source: dateSource };
          mergedData.Consultation.date_of_visit = { value: canonicalDate, confidence: 1.0, source: dateSource };
        }

        const preArrival = mergedData?.PreConsultation?.arrival_time?.value;
        const consultArrival = mergedData?.Consultation?.arrival_time?.value;
        const hasPre = preArrival != null && String(preArrival).trim() !== '';
        const hasConsult = consultArrival != null && String(consultArrival).trim() !== '';
        if (hasPre || hasConsult) {
          const canonical = hasPre ? preArrival : consultArrival;
          const source =
            mergedData?.PreConsultation?.arrival_time?.source ||
            mergedData?.Consultation?.arrival_time?.source ||
            'database';
          if (!mergedData.PreConsultation) mergedData.PreConsultation = {};
          if (!mergedData.Consultation) mergedData.Consultation = {};
          mergedData.PreConsultation.arrival_time = { value: canonical, confidence: 1.0, source };
          mergedData.Consultation.arrival_time = { value: canonical, confidence: 1.0, source };
        }
      }
      if (isActiveSession) {
        const consultation = mergedData['Consultation'] || {};
        const hasDateOfVisit =
          consultation['date_of_visit']?.value != null &&
          String(consultation['date_of_visit'].value).trim() !== '';
        if (!hasDateOfVisit) {
          const today = new Date().toISOString().split('T')[0];
          mergedData['Consultation'] = {
            ...consultation,
            date_of_visit: { value: today, confidence: 1.0, source: 'manual' },
          };
        }
      }

      if (activeSessionIdRef.current !== startedFor) {
        console.log('[useFormUpdates] Discarding stale load (session changed during fetch):', startedFor);
        return;
      }

      // Update form data with merged results. Always commit for this session — including empty merge —
      // otherwise the UI keeps showing the *previous* session's rows until a later refresh (common when
      // clicking "Edit form" on a previous visit right after another load).
      if (Object.keys(mergedData).length === 0) {
        console.log('[useFormUpdates] Empty merge for session — clearing form:', startedFor);
        setFormData({});
        lastMergedSessionIdRef.current = startedFor;
        return;
      }

      console.log('[useFormUpdates] ✅ Merged data loaded:', Object.keys(mergedData));
      setFormData((prev) => {
        const next: FormData = {};
        for (const [tabName, fields] of Object.entries(mergedData)) {
          next[tabName] = { ...(fields as object) };
        }
        // Only keep in-memory manual edits when reloading the *same* session (poll / refresh).
        // Otherwise opening another session would keep the previous session's "manual" defaults (e.g. today's date).
        const prevSessionId = lastMergedSessionIdRef.current;
        const carryManualFromPrev = prevSessionId === startedFor;
        const carryBrowserDateTimeAcrossSessionStart = !prevSessionId && !!startedFor && isActiveSession;
        if (carryManualFromPrev) {
          for (const [tabName, fields] of Object.entries(prev)) {
            if (!next[tabName]) next[tabName] = {};
            for (const [fieldName, fieldData] of Object.entries(fields)) {
              if (fieldData?.source === 'manual') {
                next[tabName][fieldName] = fieldData;
              }
            }
          }
        } else if (carryBrowserDateTimeAcrossSessionStart) {
          // NEW active visit start (null -> active session):
          // keep browser-local Pre-Consultation date/time and mirror to Consultation,
          // even if backend/session payload already has server-side values.
          const prevArrival = prev?.PreConsultation?.arrival_time;
          const prevArrivalValue = prevArrival?.value;
          if (prevArrivalValue != null && String(prevArrivalValue).trim() !== '') {
            if (!next.PreConsultation) next.PreConsultation = {};
            if (!next.Consultation) next.Consultation = {};
            next.PreConsultation.arrival_time = { ...prevArrival, source: 'manual' };
            next.Consultation.arrival_time = { ...prevArrival, source: 'manual' };
          }

          const prevVisitDate = prev?.PreConsultation?.visit_date;
          const prevVisitDateValue = prevVisitDate?.value;
          if (prevVisitDateValue != null && String(prevVisitDateValue).trim() !== '') {
            if (!next.PreConsultation) next.PreConsultation = {};
            if (!next.Consultation) next.Consultation = {};
            next.PreConsultation.visit_date = { ...prevVisitDate, source: 'manual' };
            next.Consultation.date_of_visit = { ...prevVisitDate, source: 'manual' };
          }
        }
        lastMergedSessionIdRef.current = startedFor;
        return next;
      });
    } catch (err) {
      console.error('[useFormUpdates] Error loading from database:', err);
    }
  }, [sessionId]);

  // When session is cleared or switched (e.g. patient change, or "Edit form" on another visit), reset form
  // so tabs do not keep showing the previous session until a slow fetch finishes.
  useEffect(() => {
    if (!sessionId) {
      setFormData({});
      lastMergedSessionIdRef.current = null;
      setIsConnected(false);
      setError(null);
      prevHookSessionIdRef.current = null;
      return;
    }
    const prev = prevHookSessionIdRef.current;
    if (prev !== null && prev !== sessionId) {
      setFormData({});
      lastMergedSessionIdRef.current = null;
    }
    prevHookSessionIdRef.current = sessionId;
  }, [sessionId]);

  // ==========================================
  // Initial load + WebSocket + Polling
  // ==========================================
  useEffect(() => {
    if (!sessionId) {
      console.log('[useFormUpdates] No session ID, skipping');
      return;
    }

    // Initial load from database
    loadFromDatabase();

    // Setup WebSocket for real-time updates
    console.log('[useFormUpdates] Connecting to WebSocket...');
    const wsUrl = getWebSocketUrl(`/ws/forms/${sessionId}`);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[useFormUpdates] ✅ WebSocket connected');
      setIsConnected(true);
      setError(null);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[useFormUpdates] 📥 WebSocket update:', message);

        if (message.type === 'field_update') {
          setFormData((prev) => ({
            ...prev,
            [message.tab_name]: {
              ...(prev[message.tab_name] || {}),
              [message.field_name]: {
                value: message.value,
                confidence: message.confidence || 1.0,
                source: message.source || 'ambient',
              },
            },
          }));
        } else if (message.type === 'bulk_update') {
          setFormData((prev) => {
            const updates = { ...prev };
            for (const [tabName, fields] of Object.entries(message.data || {})) {
              if (!updates[tabName]) updates[tabName] = {};
              for (const [fieldName, fieldData] of Object.entries(fields as any)) {
                updates[tabName][fieldName] = {
                  value: (fieldData as any).value,
                  confidence: (fieldData as any).confidence || 1.0,
                  source: (fieldData as any).source || 'ambient',
                };
              }
            }
            return updates;
          });
        }
      } catch (err) {
        console.error('[useFormUpdates] WebSocket message parse error:', err);
      }
    };

    ws.onerror = (err) => {
      console.error('[useFormUpdates] WebSocket error:', err);
      setError('WebSocket connection error');
      setIsConnected(false);
    };

    ws.onclose = () => {
      console.log('[useFormUpdates] WebSocket disconnected');
      setIsConnected(false);
    };

    // ==========================================
    // PERIODIC POLLING (backup sync)
    // Poll database every 5 seconds to catch any missed WebSocket updates
    // ==========================================
    pollIntervalRef.current = setInterval(() => {
      console.log('[useFormUpdates] 🔄 Periodic database sync...');
      loadFromDatabase();
    }, 5000);  // Poll every 5 seconds

    // Cleanup
    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        console.log('[useFormUpdates] Closing WebSocket');
        ws.close();
      }

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [sessionId, loadFromDatabase]);

  // Manual refresh function
  const refreshFromDatabase = useCallback(async () => {
    console.log('[useFormUpdates] 🔄 Manual refresh requested');
    await loadFromDatabase();
  }, [loadFromDatabase]);

  const saveFormData = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!sessionId) return { success: false, error: 'No session' };
    const apiBaseUrl = getApiBaseUrl();
    const payload: FormData = JSON.parse(JSON.stringify(formData || {}));
    // Keep both tabs' arrival_time synchronized at save time as well.
    const preArrival = payload?.PreConsultation?.arrival_time?.value;
    const consultArrival = payload?.Consultation?.arrival_time?.value;
    const hasPre = preArrival != null && String(preArrival).trim() !== '';
    const hasConsult = consultArrival != null && String(consultArrival).trim() !== '';
    if (hasPre || hasConsult) {
      const canonical = hasPre ? preArrival : consultArrival;
      if (!payload.PreConsultation) payload.PreConsultation = {};
      if (!payload.Consultation) payload.Consultation = {};
      payload.PreConsultation.arrival_time = { value: canonical, confidence: 1.0, source: 'manual' };
      payload.Consultation.arrival_time = { value: canonical, confidence: 1.0, source: 'manual' };
    }
    try {
      const response = await fetch(`${apiBaseUrl}/forms/${sessionId}/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': 'true' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || `Save failed: ${response.status}`);
      }
      console.log('[useFormUpdates] ✅ Form saved');
      return { success: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      console.error('[useFormUpdates] Save error:', msg);
      return { success: false, error: msg };
    }
  }, [sessionId, formData]);

  // Helper: Update field (for manual form edits)
  const updateField = useCallback((tabName: string, fieldName: string, value: any) => {
    setFormData((prev) => {
      const next: FormData = {
        ...prev,
        [tabName]: {
          ...(prev[tabName] || {}),
          [fieldName]: {
            value,
            confidence: 1.0,
            source: 'manual',
          },
        },
      };
      // Mirror shared arrival_time across both tabs so PreConsultation and Patient Visit stay in sync.
      if (
        fieldName === 'arrival_time' &&
        (tabName === 'PreConsultation' || tabName === 'Consultation')
      ) {
        const otherTab = tabName === 'PreConsultation' ? 'Consultation' : 'PreConsultation';
        next[otherTab] = {
          ...(next[otherTab] || {}),
          arrival_time: {
            value,
            confidence: 1.0,
            source: 'manual',
          },
        };
      }
      return next;
    });
  }, []);

  // Helper: Get field value
  const getFieldValue = useCallback(
    (tabName: string, fieldName: string, defaultValue: any = '') => {
      return formData[tabName]?.[fieldName]?.value ?? defaultValue;
    },
    [formData]
  );

  // Helper: Get field confidence
  const getFieldConfidence = useCallback(
    (tabName: string, fieldName: string) => {
      return formData[tabName]?.[fieldName]?.confidence ?? 1.0;
    },
    [formData]
  );

  // Helper: Get field source
  const getFieldSource = useCallback(
    (tabName: string, fieldName: string) => {
      return formData[tabName]?.[fieldName]?.source ?? 'manual';
    },
    [formData]
  );

  const hasLowConfidence = useCallback(
    (tabName: string, fieldName: string, threshold: number = 0.7) => {
      const confidence = getFieldConfidence(tabName, fieldName);
      return confidence < threshold;
    },
    [getFieldConfidence]
  );

  const clearFormForTab = useCallback((tabName: string) => {
    setFormData((prev) => {
      const next = { ...prev };
      delete next[tabName];
      return next;
    });
  }, []);

  return {
    formData,
    isConnected,
    error,
    updateField,
    getFieldValue,
    getFieldConfidence,
    getFieldSource,
    hasLowConfidence,
    refreshFromDatabase,
    saveFormData,
    clearFormForTab,
  };
}
